import { Cart, CartItem, Order } from "./types"
import type {
  PageType,
  Cart as NostoCart,
  CartItem as NostoCartItem,
  WebsiteOrder as NostoOrder
} from "@nosto/client-lib"
import { nostojs } from "./api"
import settings from "@/core/settings"

function toString<T>(value: T) {
  return typeof value === "number" ? String(value) : value
}

function lineItemToNosto(item: CartItem, currency: string): NostoCartItem {
  let itemPrice = +item.price
  if (item.discount_allocations && item.discount_allocations.length > 0) {
    const itemDiscount = item.discount_allocations
      .map(allocation => parseFloat(allocation.amount))
      .reduce((acc, d) => acc + d, 0)
    itemPrice = +(+itemPrice - itemDiscount / item.quantity).toFixed(2)
  }
  return {
    product_id: toString(item.product_id),
    sku_id: toString(item.variant_id),
    quantity: item.quantity,
    name: item.title,
    unit_price: itemPrice,
    price_currency_code: currency
  }
}

function cartToNosto(cart: Cart): NostoCart {
  const items = cart.items.filter(item => item.product_id).map(item => lineItemToNosto(item, cart.currency!))
  return { items }
}

function orderToNosto(order: Order): NostoOrder {
  const items = order.line_items.map(item => lineItemToNosto(item, order.presentment_currency || order.currency))
  return {
    info: {
      order_number: toString(order.order_id),
      email: order.email,
      first_name: order.billing_address?.first_name || "",
      last_name: order.billing_address?.last_name || "",
      type: "order"
    } as NostoOrder["info"],
    items: items
  } as NostoOrder
}

export const CART_TAGGING_KEY = "nosto:shopify:cart"

function isShopifyWebPixelLoaded() {
  return !!sessionStorage.getItem(CART_TAGGING_KEY)
}

/**
 * Register tagging provider for cart updates that are synced from the Shopify Pixel
 * to session storage
 */
export function registerCartTaggingUpdates() {
  if (!settings.usePixelAppExtension || !isShopifyWebPixelLoaded()) {
    return
  }

  registerTaggingUpdatesWithPixel()
}

function registerTaggingUpdatesWithPixel() {
  registerStorageListener()

  nostojs(api => {
    api.setTaggingProvider(
      "cart",
      () => {
        try {
          const items = sessionStorage.getItem(CART_TAGGING_KEY)
          return (items ? { items: JSON.parse(items) } : undefined) as NostoCart
        } catch (e) {
          api.internal.logger.warn("Caught error parsing cart from sessionStorage", e)
          return undefined
        }
      },
      { priority: true }
    )
  })
}

function registerStorageListener() {
  let lastValue = sessionStorage.getItem(CART_TAGGING_KEY)
  // listening on window.nosto because sessionStorage events don't trigger in the same tab (i.e window)
  window.nosto.addEventListener("storage", event => {
    if (event.key === CART_TAGGING_KEY && event.newValue !== lastValue) {
      nostojs(api => api.internal.emit("cartUpdated"))
      lastValue = event.newValue
    }
  })
}

export function updatePageTypeTagging(pageType: PageType) {
  nostojs(api => api.setTaggingProvider("pageType", pageType))
}

export function updateCategoryIdsTagging(categoryIds: string[]) {
  nostojs(api => api.setTaggingProvider("categoryIds", categoryIds))
}

export function updateCartTagging(cart: Cart) {
  const nostoCart = cartToNosto(cart)
  nostojs(api => api.setTaggingProvider("cart", nostoCart))
}

export function updateOrderTagging(order: Order) {
  const nostoOrder = orderToNosto(order)
  nostojs(api => api.setTaggingProvider("order", nostoOrder))
}
